import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { useCart } from '../../context/CartContext';
import Header from '../../components/ui/Header';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';

const Checkout = () => {
  const navigate = useNavigate();
  const { cartItems, getCartTotal } = useCart();

  const calculateSubtotal = () => {
    return getCartTotal();
  };

  const calculateTotal = () => {
    return calculateSubtotal();
  };

  const handleProceedToPayment = () => {
    // Redirect to PayPal
    window.open('https://paypal.me/INFUHG', '_blank');
  };

  // Redirect if cart is empty
  if (cartItems?.length === 0) {
    return (
      <>
        <Helmet>
          <title>Checkout - INF LABS</title>
          <meta name="description" content="Complete your order at INF LABS" />
        </Helmet>

        <div className="min-h-screen bg-[var(--color-background)]">
          <Header />
          
          <main className="pt-[100px] pb-12 px-4 md:px-6 lg:px-8">
            <div className="max-w-[1200px] mx-auto">
              <div className="text-center py-16">
                <Icon name="ShoppingCart" size={64} color="var(--color-muted-foreground)" />
                <h2 className="text-2xl font-bold text-foreground mt-4 mb-2">
                  Your cart is empty
                </h2>
                <p className="text-muted-foreground mb-6">
                  Add some products to checkout
                </p>
                <Button onClick={() => navigate('/product-catalog')} variant="default">
                  Browse Products
                </Button>
              </div>
            </div>
          </main>
        </div>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>Checkout - INF LABS</title>
        <meta name="description" content="Complete your order at INF LABS" />
      </Helmet>

      <div className="min-h-screen bg-[var(--color-background)]">
        <Header />
        
        <main className="pt-[100px] pb-12 px-4 md:px-6 lg:px-8">
          <div className="max-w-[1200px] mx-auto">
            <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-8">
              Checkout
            </h1>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Left Column - Payment Instructions */}
              <div className="lg:col-span-2 space-y-6">
                {/* Payment Instructions */}
                <div className="p-8 rounded-xl bg-primary/10 border border-primary/30">
                  <div className="flex items-start gap-4">
                    <Icon name="MessageSquare" size={32} color="var(--color-primary)" className="flex-shrink-0" />
                    <div>
                      <h3 className="text-2xl font-bold text-primary mb-4">
                        Please Do The Following Once Paid
                      </h3>
                      <p className="text-foreground text-lg mb-4">
                        If there are questions before paying, please contact us.
                      </p>
                      <div className="bg-white/50 dark:bg-black/20 p-4 rounded-lg mb-4">
                        <p className="text-foreground font-semibold mb-2">
                          After payment, text the following information to:
                        </p>
                        <p className="text-2xl font-bold text-primary mb-3">
                          801-810-4671
                        </p>
                        <ul className="list-disc list-inside text-foreground space-y-2">
                          <li>Your full name</li>
                          <li>Your full address</li>
                          <li>Your phone number</li>
                          <li>Products you purchased</li>
                        </ul>
                      </div>
                      <div className="flex items-center gap-2 p-4 rounded-lg bg-accent/10 border border-accent/30">
                        <Icon name="Truck" size={24} color="var(--color-accent)" />
                        <p className="text-foreground font-semibold">
                          Your order will be shipped the next day after confirmation.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right Column - Order Summary */}
              <div className="lg:col-span-1">
                <div className="p-6 rounded-xl bg-card border border-border sticky top-[120px]">
                  <h2 className="text-xl font-bold text-foreground mb-6 flex items-center gap-2">
                    <Icon name="ShoppingCart" size={24} color="var(--color-primary)" />
                    Order Summary
                  </h2>

                  {/* Cart Items */}
                  <div className="space-y-4 mb-6">
                    {cartItems?.map((item) => (
                      <div key={item?.id} className="flex items-center gap-3 pb-4 border-b border-border">
                        <img
                          src={item?.image}
                          alt={item?.imageAlt || item?.name}
                          className="w-16 h-16 object-cover rounded-lg"
                        />
                        <div className="flex-1">
                          <h3 className="text-sm font-semibold text-foreground">{item?.name}</h3>
                          <p className="text-xs text-muted-foreground">Qty: {item?.quantity}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-bold text-foreground">
                            ${(item?.price * item?.quantity)?.toFixed(2)}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Totals */}
                  <div className="space-y-3 mb-6">
                    <div className="flex justify-between text-foreground">
                      <span>Subtotal</span>
                      <span className="font-semibold">${calculateSubtotal()?.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-foreground">
                      <span>Shipping</span>
                      <span className="font-semibold text-accent">FREE</span>
                    </div>
                    <div className="pt-3 border-t border-border flex justify-between text-lg font-bold">
                      <span className="text-foreground">Total</span>
                      <span className="text-primary">${calculateTotal()?.toFixed(2)}</span>
                    </div>
                  </div>

                  {/* Checkout Button */}
                  <Button
                    onClick={handleProceedToPayment}
                    variant="default"
                    fullWidth
                    className="h-12 text-base font-semibold"
                  >
                    Send ${calculateTotal()?.toFixed(2)} Amount
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </>
  );
};

export default Checkout;